// src/components/admin/AdminCondoCard.tsx
// Card for listing a condo in admin view (reuses DiscoverUserCard structure)

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Condo } from '../../types/condo';

interface AdminCondoCardProps {
  condo: Condo;
}

const AdminCondoCard: React.FC<AdminCondoCardProps> = ({ condo }) => {
  const navigate = useNavigate();

  const condoId = condo.id || condo._id || '';
  const primaryImage = condo.images && condo.images.length > 0
    ? condo.images[0]
    : 'https://via.placeholder.com/400x240?text=No+Image';
  const locationText = [
    condo.location?.neighborhood,
    condo.location?.borough
  ].filter(Boolean).join(', ') || condo.location?.borough || '—';
  const priceText = condo.price != null ? `$${condo.price.toLocaleString()} / mo` : '—';
  const bedroomsText = condo.bedrooms === 0 ? 'Studio' : condo.bedrooms != null ? `${condo.bedrooms} bed` : null;
  const bathroomsText = condo.bathrooms != null ? `${condo.bathrooms} bath` : null;
  const sqftText = condo.sqft != null ? `${condo.sqft.toLocaleString()} sqft` : null;
  const unitsSummary = [bedroomsText, bathroomsText, sqftText].filter(Boolean).join(' · ') || '—';

  const handleEdit = () => {
    if (condoId) {
      navigate(`/admin/condos/edit/${condoId}`);
    }
  };

  return (
    <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
      {/* Primary image */}
      <div className="relative mb-4 rounded-lg overflow-hidden border-2 border-coffee">
        <img
          src={primaryImage}
          alt={condo.name}
          className="w-full h-48 object-cover"
        />
      </div>

      {/* Name */}
      <h3 className="text-coffee font-bold text-lg mb-2">
        {condo.name}
      </h3>

      {/* Location */}
      <div className="mb-3">
        <span className="text-coffee/80 font-medium text-sm">Location: </span>
        <span className="text-coffee text-sm">{locationText}</span>
      </div>

      {/* Price */}
      <div className="mb-3">
        <span className="text-coffee/80 font-medium text-sm">Price: </span>
        <span className="text-coffee text-sm">{priceText}</span>
      </div>

      {/* Unit details */}
      <div className="mb-4">
        <span className="text-coffee text-sm">{unitsSummary}</span>
      </div>

      {/* Edit button */}
      <button
        type="button"
        onClick={handleEdit}
        className="w-full text-sm bg-terracotta text-white border-2 border-coffee rounded-lg shadow-[2px_2px_0px_#594A42] hover:translate-y-[-1px] active:translate-y-[1px] active:shadow-none transition-all disabled:opacity-50 px-3 py-2 font-medium flex items-center justify-center gap-1.5"
      >
        <Pencil className="h-4 w-4 shrink-0" />
        Edit
      </button>
    </div>
  );
};

export default AdminCondoCard;
